import React from 'react';
import { shape, bool, string, func } from 'prop-types';
import { connect } from 'react-redux';
import { gql, graphql, compose } from 'react-apollo';

import { operations } from 'state/ducks/map';

import formatMinutesSeconds from 'helpers/formatMinutesSeconds';
import formatNextUpdate from 'helpers/formatNextUpdate';
import formatLastUpdate from 'helpers/formatLastUpdate';
import formatInteger from 'helpers/formatInteger';
import formatFloat from 'helpers/formatFloat';
import cx from 'classnames';

import IconButton from 'components/IconButton';
import ChevronIcon from 'icons/chevron.inline.svg';

import LoadingIndicator from 'components/LoadingIndicator';
import TimeDisplay from 'components/TimeDisplay';
import SpeedDisplay from 'components/SpeedDisplay';

import RouteSegments from '../RouteSegments';

import Layout from '../Layout';
import RouteHeader from '../RouteHeader';
import TripletRow from '../../TripletRow';
import TripletItem from '../../TripletItem';
import HorizontalSection from '../../HorizontalSection';
import GridRow from '../../GridRow';
import GridItem from '../../GridItem';
import Chart from '../Chart';
import SpeedTrendIcon from '../../../SpeedTrendIcon';

import styles from './route.scss';

const RouteQuery = gql`
  query Route($id: ID!) {
    route(id: $id) {
      id
      status
      liveSpeed
      normalSpeed
      speedLimit
      tripsToday
      liveTravelTime
      expectedTravelTime
      travelTimeIndex
      numberOfTripsToday
      numberOfTripsSinceLastUpdate
      oppositeRouteId
      liveTravelTimeToday {
        timestamp
        value
      }
      expectedTravelTimeToday {
        timestamp
        value
      }
      from {
        id
        name
        trackingDevice {
          id
          lastUpdate
          reportInterval
        }
      }
      to {
        id
        name
        trackingDevice {
          id
          lastUpdate
          reportInterval
        }
      }
      segments {
        id
        status
        expectedTravelTime
        travelTimeIndex
        numberOfTripsToday
        numberOfTripsSinceLastUpdate
        liveSpeed
        speedLastHour
        from {
          id
          name
        }
        to {
          id
          name
        }
      }
    }
  }
`;

export function Route({ data, selectEntity, enterEntity, leaveEntity }) {
  if (data.loading) {
    return (
      <Layout
        backTo="/map/live/routes"
        title="Routes"
        header={<RouteHeader status="ok" fromName="..." toName="..." isLoading />}
      >
        <LoadingIndicator />
      </Layout>
    );
  }

  if (!data.route) {
    return (
      <div>
        Failed to find route for id: {data.variables.id}
      </div>
    );
  }

  const {
    status,
    oppositeRouteId,
    from: { trackingDevice: fromDevice, name: fromLocationName },
    to: { trackingDevice: toDevice, name: toLocationName },
    liveTravelTime,
    expectedTravelTime,
    liveTravelTimeToday,
    expectedTravelTimeToday,
    travelTimeIndex,
    liveSpeed,
    normalSpeed,
    speedLimit,
    segments,
    id,
  } = data.route;

  const isReporting = status !== 'disconnected';
  const reportInterval = Math.max(fromDevice.reportInterval, toDevice.reportInterval);
  const lastUpdate = fromDevice.lastUpdate;
  const speedTrend = 'up';

  const numberOfTripsToday = segments.reduce((acc, curr) => acc + curr.numberOfTripsToday, 0);
  const numberOfTripsSinceLastUpdate = segments.reduce(
    (acc, curr) => acc + curr.numberOfTripsSinceLastUpdate,
    0
  );

  return (
    <Layout
      backTo="/map/live/routes"
      title="All routes"
      header={
        <RouteHeader
          status={status}
          fromName={fromLocationName}
          toName={toLocationName}
          oppositeRouteId={oppositeRouteId}
        />
      }
    >
      {isReporting
        ? <TripletRow title="Travel time">
            <TripletItem title="live" unitName="min" status={status}>
              <TimeDisplay value={liveTravelTime} status={status} />
              <SpeedTrendIcon
                trend={speedTrend}
                className={cx(styles.trendIcon, {
                  [styles[`status-${status}`]]: status,
                })}
              />
            </TripletItem>
            <TripletItem title="expected" unitName="min">
              <TimeDisplay value={expectedTravelTime} />
            </TripletItem>
            <TripletItem title="index">
              {formatFloat(travelTimeIndex)}
            </TripletItem>
          </TripletRow>
        : <TripletRow title="Travel time">
            <TripletItem title="live" unitName="min" status={status}>
              <span>-</span>
            </TripletItem>
            <TripletItem title="expected" unitName="min" status={status}>
              <span>-</span>
            </TripletItem>
            <TripletItem title="index" status={status}>
              <span>-</span>
            </TripletItem>
          </TripletRow>}

      <Chart
        reportedValues={liveTravelTimeToday}
        expectedValues={expectedTravelTimeToday}
        calculateCrosshairA={({ reported }) => ({
          value: formatMinutesSeconds(reported, {
            useClockFormat: true,
          }),
          title: 'TT',
        })}
        calculateCrosshairB={({ reported, expected }) => ({
          value: formatFloat(reported / expected),
          title: 'TTI',
        })}
      />

      <GridRow title="Speed">
        <GridItem label="live speed" status={status}>
          {isReporting
            ? <SpeedDisplay status={status} value={liveSpeed} unit="MPH" trend={speedTrend} />
            : '-'}
        </GridItem>
        <GridItem label="speed limit">
          <SpeedDisplay value={speedLimit} unit="MPH" />
        </GridItem>
        <GridItem label="expected speed">
          <SpeedDisplay value={normalSpeed} unit="MPH" />
        </GridItem>
      </GridRow>
      <GridRow title="Updates">
        <GridItem label="trips today">
          {formatInteger(numberOfTripsToday)}
        </GridItem>
        <GridItem label="trips last update">
          {formatInteger(numberOfTripsSinceLastUpdate)}
        </GridItem>
        {isReporting
          ? <GridItem label="next update">
              {formatNextUpdate(lastUpdate, reportInterval)}
            </GridItem>
          : <GridItem label="last update">
              {formatLastUpdate(lastUpdate)}
            </GridItem>}
      </GridRow>

      <RouteSegments
        title="segments in route"
        segments={segments}
        selectEntity={selectEntity}
        enterEntity={enterEntity}
        leaveEntity={leaveEntity}
      />

      <HorizontalSection center>
        <IconButton label="details page" linkTo={`/segments/${id}`} disabled asLink iconRight>
          <ChevronIcon />
        </IconButton>
      </HorizontalSection>
    </Layout>
  );
}

Route.propTypes = {
  data: shape({
    loading: bool.isRequired,
    location: shape({}),
    route: shape({
      id: string.isRequired,
    }),
  }).isRequired,
  selectEntity: func.isRequired,
  enterEntity: func.isRequired,
  leaveEntity: func.isRequired,
};

const mapStateToProps = (_, ownProps) => ({
  ...ownProps,
});

const ConnectedRouteWithData = compose(
  connect(mapStateToProps, { ...operations }),
  graphql(RouteQuery, {
    options: ({ params }) => ({
      variables: {
        id: params.id,
      },
    }),
  })
)(Route);

export default ConnectedRouteWithData;



// WEBPACK FOOTER //
// ./src/components/DataPanel/Details/Route/index.js